import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import AddressAutocomplete from "../AddressAutocomplete";
import ClientPropertyPicker from "../ClientPropertyPicker";
import { FileDropZone } from "../FileDropZone";
import { OOH_CATEGORIES } from "./OohEditCaseDialog";
import { Moon, Siren } from "@phosphor-icons/react";

export const MANUAL_SOURCES = [["phone", "Phone call-in"], ["whatsapp", "WhatsApp"], ["text", "Text message"], ["email_forward", "Email forwarded to me"], ["in_person", "In person"], ["client_portal", "Client call / portal"], ["other", "Other"]];
const SEVERITIES = [["", "Not sure — let the AI decide"], ["critical", "Critical — people or property at risk now"], ["urgent", "Urgent — needs sorting tonight"], ["non_urgent", "Non-urgent — can wait for the day team"]];
const NONE = "__none__";

export const emptyRequest = () => ({
  source: "phone", title: "", category: "General", severity: "", raise_emergency: false, description: "",
  client_id: "", client_name: "", client_company: "", client_email: "", client_phone: "", requester_contact_id: "", requester_contact_name: "",
  property_id: "", property_name: "", property_address: "", street_address: "", town_city: "", county: "", postcode: "", latitude: null, longitude: null, what3words: "",
  requester_name: "", requester_phone: "", requester_email: "", access_notes: "", attachments: [],
});

function ClientBlock({ f, set, clients, contacts, setContacts, prefix }) {
  const pickClient = (id) => {
    if (!id || id === NONE) { set({ ...f, client_id: "", client_name: "", client_company: "", client_email: "", client_phone: "", requester_contact_id: "", requester_contact_name: "", property_id: "", property_name: "" }); setContacts([]); return; }
    const c = clients.find((x) => x.id === id);
    if (!c) return;
    set({ ...f, client_id: c.id, client_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(), client_company: c.company_name || "", client_email: c.email || "", client_phone: c.phone || "", requester_contact_id: "", requester_contact_name: "",
      requester_name: f.requester_name || `${c.first_name || ""} ${c.last_name || ""}`.trim(), requester_phone: f.requester_phone || c.phone || "", requester_email: f.requester_email || c.email || "" });
    setContacts(c.contacts || []);
  };
  const pickContact = (id) => {
    if (!id || id === NONE) { set({ ...f, requester_contact_id: "", requester_contact_name: "" }); return; }
    const c = contacts.find((x) => x.id === id);
    if (c) set({ ...f, requester_contact_id: c.id, requester_contact_name: `${c.first_name} ${c.last_name}`, requester_name: `${c.first_name} ${c.last_name}`, requester_phone: c.phone || f.requester_phone, requester_email: c.email || f.requester_email });
  };
  return (
    <div className="space-y-3">
      <div>
        <Label>Client</Label>
        <Select value={f.client_id || NONE} onValueChange={pickClient}>
          <SelectTrigger data-testid={`${prefix}-client-select`}><SelectValue placeholder="Select a client…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>No client — private / unknown caller</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name ? `${c.company_name} — ${c.first_name} ${c.last_name}` : `${c.first_name} ${c.last_name}`}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {f.client_id && contacts.length > 0 && (
        <div>
          <Label>Who at the client is asking?</Label>
          <Select value={f.requester_contact_id || NONE} onValueChange={pickContact}>
            <SelectTrigger data-testid={`${prefix}-requester-select`}><SelectValue placeholder="Tag a contact…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Not a listed contact</SelectItem>
              {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.job_role ? ` (${c.job_role})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {!f.client_id && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Client / landlord name</Label><Input value={f.client_name} onChange={(e) => set({ ...f, client_name: e.target.value })} data-testid={`${prefix}-client-name`} /></div>
          <div><Label>Company</Label><Input value={f.client_company} onChange={(e) => set({ ...f, client_company: e.target.value })} data-testid={`${prefix}-client-company`} /></div>
        </div>
      )}
    </div>
  );
}

export default function OohNewCaseDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(emptyRequest());
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(emptyRequest()); setContacts([]);
    API.get("/clients?limit=500").then(({ data }) => setClients(data.clients || [])).catch(() => {});
  }, [open]);
  const up = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const applyProperty = (p) => setF((x) => p ? ({ ...x, property_id: p.id, property_name: p.name || "", property_address: [p.address_line, p.town_city, p.county, p.postcode].filter(Boolean).join(", "),
    street_address: p.address_line || "", town_city: p.town_city || "", county: p.county || "", postcode: p.postcode || "", what3words: p.what3words || x.what3words,
    // the property's own site contact is who the desk should phone — it beats whatever the client pick filled in
    ...(p.site_contact_name ? { requester_name: p.site_contact_name, requester_phone: p.site_contact_phone || x.requester_phone, requester_email: p.site_contact_email || x.requester_email } : {}),
    access_notes: p.access_notes || x.access_notes }) : ({ ...x, property_id: "", property_name: "" }));
  const save = async (e) => {
    e?.preventDefault?.();
    if (!f.title.trim()) { toast.error("Give the request a short title"); return; }
    if (!f.requester_name.trim()) { toast.error("Who is asking? The requester name is required"); return; }
    if (!f.requester_phone.trim() && !f.requester_email.trim()) { toast.error("A phone number or email for the requester is required"); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/ooh/cases", f);
      toast.success(data.emergency_job ? `${data.reference} logged — emergency job ${data.emergency_job.reference} raised` : `${data.reference} logged — AI triage is on its way`);
      onOpenChange(false);
      onCreated(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't log the request"); }
    finally { setBusy(false); }
  };
  const P = "ooh-new";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="ooh-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2"><Moon size={18} weight="duotone" className="text-indigo-600" /> New out of hours request</DialogTitle>
          <DialogDescription>Log it exactly as you would a normal job request. It lands on the OOH desk, the AI writes the triage script straight away, and you decide call-out or make-safe from there.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-[220px_1fr] gap-3">
            <div>
              <Label>How did it come in?</Label>
              <Select value={f.source} onValueChange={(v) => setF({ ...f, source: v })}>
                <SelectTrigger data-testid={`${P}-source`}><SelectValue /></SelectTrigger>
                <SelectContent>{MANUAL_SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Title *</Label><Input value={f.title} onChange={up("title")} required placeholder="e.g. No heating or hot water — boiler showing F22, 14 Oak Road" data-testid={`${P}-title`} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid={`${P}-category`}><SelectValue /></SelectTrigger>
                <SelectContent>{OOH_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reported as</Label>
              <Select value={f.severity || NONE} onValueChange={(v) => setF({ ...f, severity: v === NONE ? "" : v, raise_emergency: v === "critical" ? f.raise_emergency : false })}>
                <SelectTrigger data-testid={`${P}-severity`}><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(([k, l]) => <SelectItem key={k || NONE} value={k || NONE}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {f.severity === "critical" && (
            <label className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm" data-testid={`${P}-raise-emergency-row`}>
              <Checkbox checked={f.raise_emergency} onCheckedChange={(v) => setF({ ...f, raise_emergency: !!v })} data-testid={`${P}-raise-emergency`} />
              <span><span className="font-semibold text-red-800 dark:text-red-200 inline-flex items-center gap-1"><Siren size={14} weight="fill" /> Raise the emergency job straight away</span><br /><span className="text-xs text-muted-foreground">Critical, 4h SLA — the case opens as a red call-out with the contractor finder ready. Leave unticked to triage first.</span></span>
            </label>
          )}
          <div><Label>Request details *</Label><Textarea rows={4} value={f.description} onChange={up("description")} placeholder="What's happening, when it started, what they've tried, anyone vulnerable in the home, access…" data-testid={`${P}-description`} /></div>

          <ClientBlock f={f} set={setF} clients={clients} contacts={contacts} setContacts={setContacts} prefix={P} />
          <ClientPropertyPicker clientId={f.client_id} clientLabel={f.client_company || f.client_name} propertyId={f.property_id} onPick={applyProperty} testid={`${P}-client-property`} />
          <div>
            <Label>Property address</Label>
            <AddressAutocomplete value={f.property_address} onChange={(v) => setF({ ...f, property_address: v })}
              onSelect={(a) => setF((x) => ({ ...x, property_address: a.formatted_address || x.property_address, street_address: a.street_address || "", town_city: a.town_city || "", county: a.county || "", postcode: a.postcode || "", latitude: a.latitude ?? null, longitude: a.longitude ?? null }))}
              placeholder="42 High Street, London, SW1A 1AA" data-testid={`${P}-address`} />
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Input value={f.town_city} onChange={up("town_city")} placeholder="Town / city" data-testid={`${P}-town`} />
              <Input value={f.postcode} onChange={up("postcode")} placeholder="Postcode" data-testid={`${P}-postcode`} />
              <Input value={f.what3words} onChange={up("what3words")} placeholder="///what.three.words" className="font-mono" data-testid={`${P}-what3words`} />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">REQUESTER / SITE CONTACT — who the desk phones back</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Name *</Label><Input value={f.requester_name} onChange={up("requester_name")} data-testid={`${P}-requester-name`} /></div>
              <div><Label>Phone</Label><Input value={f.requester_phone} onChange={up("requester_phone")} placeholder="+44 7…" data-testid={`${P}-requester-phone`} /></div>
              <div><Label>Email</Label><Input type="email" value={f.requester_email} onChange={up("requester_email")} placeholder="for the make-safe steps email" data-testid={`${P}-requester-email`} /></div>
            </div>
          </div>
          <div><Label>Access notes</Label><Textarea rows={2} value={f.access_notes} onChange={up("access_notes")} placeholder="Key safe, gate code, parking, dog…" data-testid={`${P}-access-notes`} /></div>
          <div>
            <Label>Photos / files</Label>
            <div className="mt-1"><FileDropZone files={f.attachments} onFilesChange={(files) => setF((x) => ({ ...x, attachments: files }))} folder="ooh" accept="all" maxFiles={10} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid={`${P}-save`}>{busy ? "Logging…" : f.severity === "critical" && f.raise_emergency ? "Log & raise emergency job" : "Log OOH request"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
